import { inject, Injectable, Signal } from '@angular/core';
import { Store } from '@ngrx/store';

import { ProductViewTemplateModel } from '../models/product-view-template.model';

import { ProductViewService } from './product-view.service';

import { GridElemType } from '@core/domain/types/product.types';
import { getCmsConfigValue } from '@core/infrastructure/state/core.selectors';
import { ProductPublicModels } from '@features/products/product';
import { PublicCartProductTypes } from '@features/shopping-cart';
import { UiService } from '@layout/ui/services/ui.service';
import { GoToCategory, GoToProduct, GoToSection, physicalGiftCard, virtualGiftCard } from '@shared/navigation';

@Injectable()
export class ProductViewNavigationService {
    private readonly store = inject(Store);

    private readonly uiService = inject(UiService);

    private readonly productViewService = inject(ProductViewService);

    public isNewTab$$: Signal<boolean> = this.store.selectSignal(
        getCmsConfigValue('product_detail_new_tab'),
    );

    public navigate(
        product: ProductPublicModels.PublicProduct,
        config: ProductViewTemplateModel,
        categoryId: string,
        clickBannerArea?: ProductPublicModels.PublicBannerLink,
    ) {
        const { productLink, categoryLink } = this.getBannerLink(product, clickBannerArea);

        if (config.clickable && !this.isNewTab$$()) {
            this.productViewService.productAnalytics(product, config, config.hasVideo,);
            this.handleUrlLink(product, categoryId, productLink, categoryLink,);
        } else if (
            product.productType === PublicCartProductTypes.PHYSICAL_CARD
            || product.productType === PublicCartProductTypes.VIRTUAL_CARD
        ) {
            this.handleCardNavigation(
                product.productType === PublicCartProductTypes.PHYSICAL_CARD,
                product.productType === PublicCartProductTypes.VIRTUAL_CARD
            );
        } else {
            this.store.dispatch(GoToSection({ url: product.locationPath, newWindow: true }));
        }
    }

    private getBannerLink(
        product: ProductPublicModels.PublicProduct,
        clickBannerArea?: ProductPublicModels.PublicBannerLink
    ): { productLink?: string; categoryLink?: string } {
        if (clickBannerArea) {
            return {
                productLink: clickBannerArea.datatype === 'productLink' ? clickBannerArea.id : undefined,
                categoryLink: clickBannerArea.datatype === 'categoryLink' ? clickBannerArea.id : undefined,
            };
        }

        const isBannerStudio = product.gridElemType === GridElemType.BANNER;
        const bannerLinkFound = product.attributes.find(attribute =>
            attribute.name.includes('BANNERLINK'));
        const bannerProductLinkStudio = product.bannerLinks.find(b => b.datatype === 'productLink');
        const bannerCategoryLinkStudio = product.bannerLinks.find(b => b.datatype === 'categoryLink');

        return {
            productLink: isBannerStudio && bannerProductLinkStudio ? bannerProductLinkStudio.id : undefined,
            categoryLink: bannerLinkFound?.value
                || (isBannerStudio && bannerCategoryLinkStudio ? bannerCategoryLinkStudio.id : undefined),
        };
    }

    private handleUrlLink(
        product: ProductPublicModels.PublicProduct,
        categoryId: string,
        productLink?: string,
        categoryLink?: string,
    ) {
        const urlLinkFound = product.attributes.find((attribute) =>
            attribute.name.includes('URLLINK'),);
        if (urlLinkFound) {
            this.redirectBasedOnAttributeLink(
                urlLinkFound,
                product.productType === PublicCartProductTypes.PHYSICAL_CARD,
                product.productType === PublicCartProductTypes.VIRTUAL_CARD
            );
        } else {
            this.handleNavigation(product, categoryId, productLink, categoryLink,);
        }
    }

    private handleNavigation(
        product: ProductPublicModels.PublicProduct,
        categoryId: string,
        productLink?: string,
        categoryLink?: string,
    ) {
        this.uiService.setPageYOffsetSubject({ productIds: product.id.toString() });
        if (categoryLink) {
            this.store.dispatch(GoToCategory({ categoryId: categoryLink }));
        } else {
            this.store.dispatch(
                GoToProduct({
                    productId: productLink || product.id,
                    categoryId,
                    colorId: !productLink ? product.status.selectedColor.id : undefined,
                    styleId: product.xmediaDefaultSet
                        ? product.xmediaDefaultSet.toString()
                        : null,
                    locationPath: product.locationPath,
                    sectionNameEn: product.sectionNameEN,
                }),
            );
        }
    }

    private handleCardNavigation(
        isPhysicalCard: boolean,
        isVirtualCard: boolean,
    ) {
        let url = '';
        if (isPhysicalCard) {
            url = physicalGiftCard;
        } else if (isVirtualCard) {
            url = virtualGiftCard;
        }
        if (url) {
            this.store.dispatch(GoToSection({ url }));
        }
    }

    private redirectBasedOnAttributeLink(
        urlLinkFound: any,
        isPhysicalCard: boolean,
        isVirtualCard: boolean,
    ) {
        const urlValue = urlLinkFound.value;
        if (urlValue.startsWith('/')) {
            if (isPhysicalCard || isVirtualCard) {
                this.handleCardNavigation(isPhysicalCard, isVirtualCard);
            } else {
                this.store.dispatch(GoToSection({ url: urlValue.slice(1) }));
            }
        } else if (urlValue.startsWith('http') || urlValue.startsWith('www')) {
            this.store.dispatch(GoToSection({ url: urlValue, newWindow: true }));
        }
    }
}
